import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  BarChart3, 
  TrendingUp, 
  Download, 
  RefreshCcw,
  DollarSign,
  Users,
  Package,
  AlertCircle
} from 'lucide-react';
import { 
  reportsService, 
  SalesReport, 
  FinancialSummary, 
  CustomerReport, 
  InventoryReport, 
  DuesReport, 
  TopProductReport 
} from '../services/reportsService';
import { customToast } from '../utils/customToast';
import { useTitle } from '../hooks/useTitle';
import { useCurrency } from '../hooks/useCurrency';

const ReportsPage: React.FC = () => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  
  // Set page title
  useTitle('Reports');

  // Utility function to format customer type as title case
  const formatCustomerType = (type: string): string => {
    const typeMap: {[key: string]: string} = {
      'retail': 'Retail',
      'home': 'Home',
      'wholesale': 'Wholesale',
      'business': 'Business',
      'regular': 'Regular',
      'vip': 'VIP'
    };
    return typeMap[type.toLowerCase()] || type;
  };

  // State management
  const [activeReport, setActiveReport] = useState<'overview' | 'sales' | 'financial' | 'inventory' | 'customers' | 'dues'>('overview');
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState<{[key: string]: boolean}>({});
  const [customerType, setCustomerType] = useState<'all' | 'retail' | 'home' | 'wholesale' | 'business' | 'regular' | 'vip'>('all');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });

  // Report data
  const [salesReport, setSalesReport] = useState<SalesReport[]>([]);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);
  const [customerReport, setCustomerReport] = useState<CustomerReport[]>([]);
  const [inventoryReport, setInventoryReport] = useState<InventoryReport[]>([]);
  const [duesReport, setDuesReport] = useState<DuesReport[]>([]);
  const [topProducts, setTopProducts] = useState<TopProductReport[]>([]);

  // Load initial data
  useEffect(() => {
    loadOverviewData();
  }, []);

  // Reload current report when customerType changes
  useEffect(() => {
    if (activeReport !== 'inventory') {
      if (activeReport === 'overview') {
        loadOverviewData();
      } else {
        loadReportData(activeReport);
      }
    }
  }, [customerType]);

  const loadOverviewData = async () => {
    setLoading(true);
    let hasErrors = false;

    // Load each report independently to avoid one failure breaking all
    try {
      const financial = await reportsService.getFinancialSummary({ 
        startDate: dateRange.startDate, 
        endDate: dateRange.endDate,
        customerType: customerType
      });
      setFinancialSummary(financial);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      hasErrors = true;
      setFinancialSummary(null);
    }

    try {
      const dues = await reportsService.getDuesReport({ 
        startDate: dateRange.startDate, 
        endDate: dateRange.endDate,
        customerType: customerType 
      });
      setDuesReport(dues);
    } catch (error) {
      console.error('Error fetching dues report:', error);
      hasErrors = true;
      setDuesReport([]);
    }

    try {
      const topProds = await reportsService.getTopProducts(5);
      setTopProducts(topProds);
    } catch (error) {
      console.error('Error fetching top products report:', error);
      hasErrors = true;
      setTopProducts([]);
    }

    if (hasErrors) {
      customToast.error('Some reports failed to load. Please check your connection and try refreshing.');
    }

    setLoading(false);
  };

  const loadReportData = async (reportType: typeof activeReport) => {
    setLoading(true);
    try {
      const filters = { startDate: dateRange.startDate, endDate: dateRange.endDate, customerType };

      switch (reportType) {
        case 'sales':
          const sales = await reportsService.getSalesReport('daily', filters);
          setSalesReport(sales);
          break;
        case 'financial':
          const financial = await reportsService.getFinancialSummary(filters);
          setFinancialSummary(financial);
          break;
        case 'customers':
          const customers = await reportsService.getCustomerReport(filters);
          setCustomerReport(customers);
          break;
        case 'inventory':
          const inventory = await reportsService.getInventoryReport();
          setInventoryReport(inventory);
          break;
        case 'dues':
          const dues = await reportsService.getDuesReport(filters);
          setDuesReport(dues);
          break;
      }
    } catch (error) {
      console.error(`Failed to load ${reportType} data:`, error);
      customToast.error(`Failed to load ${reportType} report`);
    } finally {
      setLoading(false);
    }
  };

  const handleReportChange = (reportType: typeof activeReport) => {
    setActiveReport(reportType);
    if (reportType !== 'overview') {
      loadReportData(reportType);
    } else {
      loadOverviewData();
    }
  };

  const handleExport = async (reportType: string, format: 'pdf' | 'csv') => {
    // Handle the overview case by defaulting to sales report
    const actualReportType = reportType === 'overview' ? 'sales' : reportType;
    
    // Set loading state for this specific export
    setLoadingStates(prev => ({
      ...prev,
      [`export_${actualReportType}_${format}`]: true
    }));

    try {
      const result = await reportsService.exportReport(
        actualReportType as any,
        format,
        { 
          startDate: dateRange.startDate, 
          endDate: dateRange.endDate,
          customerType: customerType 
        }
      );
      
      // Create professional download notification
      const formatName = format.toUpperCase();
      const reportName = actualReportType.charAt(0).toUpperCase() + actualReportType.slice(1);
      
      customToast.success(
        `${formatName} export generated successfully!`,
        `${reportName} report is ready for download`
      );

      // Auto-download the file
      if (result.downloadUrl && result.downloadUrl !== '#') {
        // Create a temporary anchor element for download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
    } catch (error) {
      console.error('Export failed:', error);
      customToast.error(
        'Export failed', 
        'Unable to generate report. Please check your connection and try again.'
      );
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({
        ...prev,
        [`export_${actualReportType}_${format}`]: false
      }));
    }
  };

  const handleCustomerReportExport = async (customerId: number, customerName: string, format: 'pdf' | 'csv') => {
    // Set loading state for this specific customer export
    setLoadingStates(prev => ({
      ...prev,
      [`customer_${customerId}_${format}`]: true
    }));

    try {
      const result = await reportsService.exportCustomerReport(
        customerId,
        format,
        { startDate: dateRange.startDate, endDate: dateRange.endDate }
      );
      
      // Create professional download notification
      const formatName = format.toUpperCase();
      
      customToast.success(
        `${formatName} export generated successfully!`,
        `Report for ${customerName} is ready for download`
      );

      // Auto-download the file
      if (result.downloadUrl && result.downloadUrl !== '#') {
        // Create a temporary anchor element for download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
    } catch (error) {
      console.error('Customer export failed:', error);
      customToast.error(
        'Export failed', 
        `Unable to generate report for ${customerName}. Please try again.`
      );
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({
        ...prev,
        [`customer_${customerId}_${format}`]: false
      }));
    }
  };

  const renderOverview = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <div className={`p-4 sm:p-6 rounded-lg sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Total Revenue
              </p>
              <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {financialSummary ? formatCurrency(financialSummary.total_revenue) : 'N/A'}
              </p>
            </div>
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${
              theme === 'dark' ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'
            }`}>
              <DollarSign size={20} className="sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>

        <div className={`p-4 sm:p-6 rounded-lg sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Net Profit
              </p>
              <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                theme === 'dark' ? 'text-green-400' : 'text-green-600'
              }`}>
                {financialSummary ? formatCurrency(financialSummary.net_profit) : 'N/A'}
              </p>
            </div>
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${
              theme === 'dark' ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
            }`}>
              <TrendingUp size={20} className="sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>

        <div className={`p-4 sm:p-6 rounded-lg sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Pending Dues
              </p>
              <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
              }`}>
                {formatCurrency(duesReport.reduce((sum, due) => sum + due.total_due, 0))}
              </p>
            </div>
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${
              theme === 'dark' ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'
            }`}>
              <AlertCircle size={20} className="sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>

        <div className={`p-4 sm:p-6 rounded-lg sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs sm:text-sm font-medium ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Profit Margin
              </p>
              <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
              }`}>
                {financialSummary ? `${financialSummary.profit_margin || 0}%` : 'N/A'}
              </p>
            </div>
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${
              theme === 'dark' ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'
            }`}>
              <BarChart3 size={20} className="sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Top Products & Dues */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`p-6 rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Top Selling Products
          </h3>
          <div className="space-y-4">
            {topProducts.length > 0 ? (
              topProducts.slice(0, 5).map((product, index) => (
                <div key={product.product_id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                      index === 0 
                        ? 'bg-yellow-500 text-white'
                        : theme === 'dark' 
                          ? 'bg-slate-700 text-gray-300' 
                          : 'bg-gray-100 text-gray-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className={`font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {product.product_name}
                      </p>
                      <p className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {product.quantity_sold} units • {product.brand}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      theme === 'dark' ? 'text-green-400' : 'text-green-600'
                    }`}>
                      {formatCurrency(product.revenue)}
                    </p>
                    <p className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Profit: {formatCurrency(product.profit)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className={`text-center py-8 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <Package size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No product data available</p>
                <button
                  onClick={() => loadOverviewData()}
                  className="text-blue-500 hover:text-blue-600 text-xs mt-1"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={`p-6 rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Outstanding Dues
          </h3>
          <div className="space-y-4">
            {duesReport.length > 0 ? (
              duesReport.slice(0, 5).map((due) => (
                <div key={due.customer_id} className={`p-3 rounded-xl ${
                  due.days_overdue > 7 
                    ? theme === 'dark' 
                      ? 'bg-red-900/20 border border-red-800' 
                      : 'bg-red-50 border border-red-200'
                    : theme === 'dark' 
                      ? 'bg-slate-700/50' 
                      : 'bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {due.customer_name}
                      </p>
                      <div className="flex items-center space-x-2 text-xs">
                        {due.phone && (
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                            • {due.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-500">
                        {formatCurrency(due.total_due)}
                      </p>
                      <p className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {due.sales_count || 0} sales
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={`text-center py-8 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <AlertCircle size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No outstanding dues data</p>
                <button
                  onClick={() => loadOverviewData()}
                  className="text-blue-500 hover:text-blue-600 text-xs mt-1"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Reports & Analytics
          </h1>
          <p className={`mt-1 sm:mt-2 text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Comprehensive business insights and detailed reports.
          </p>
        </div>
        
        <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 sm:items-center">
          {/* Date Range */}
          <div className="flex flex-row items-center space-x-2">
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className={`flex-1 px-2 sm:px-3 py-2.5 sm:py-2 rounded-lg sm:rounded-xl border text-xs sm:text-sm touch-manipulation ${
                theme === 'dark'
                  ? 'bg-slate-700 border-slate-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
            <span className={`text-xs sm:text-sm flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className={`flex-1 px-2 sm:px-3 py-2.5 sm:py-2 rounded-lg sm:rounded-xl border text-xs sm:text-sm touch-manipulation ${
                theme === 'dark'
                  ? 'bg-slate-700 border-slate-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {/* Customer Type Filter */}
          <select
            value={customerType}
            onChange={(e) => {
              const newValue = e.target.value as any;
              // If clicking the same value, reset to 'all'
              if (newValue === customerType) {
                setCustomerType('all');
                e.target.value = 'all';
              } else {
                setCustomerType(newValue);
              }
            }}
            className={`px-2 sm:px-3 py-2.5 sm:py-2 rounded-lg sm:rounded-xl border text-xs sm:text-sm touch-manipulation ${
              theme === 'dark'
                ? 'bg-slate-700 border-slate-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="all">All Customer Types</option>
            <option value="retail">Retail</option>
            <option value="home">Home</option>
            <option value="wholesale">Wholesale</option>
            <option value="business">Business</option>
            <option value="regular">Regular</option>
            <option value="vip">VIP</option>
          </select>

          <div className="flex space-x-2">
            {/* Refresh */}
            <button
              onClick={() => handleReportChange(activeReport)}
              disabled={loading}
              className={`flex items-center justify-center space-x-2 px-3 py-2.5 sm:py-2 rounded-lg sm:rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation flex-1 sm:flex-none ${
                loading
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 hover:border-gray-500'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              <RefreshCcw size={14} className={loading ? 'animate-spin' : ''} />
              <span className="text-sm">Refresh</span>
            </button>

            {/* Export */}
            <div className="relative group flex-1 sm:flex-none">
              <button className="w-full sm:w-auto flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-blue-500 text-white rounded-lg sm:rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation">
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
              
              {/* Export Dropdown */}
              <div className={`absolute right-0 top-full mt-2 w-full sm:w-52 rounded-lg sm:rounded-xl border shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-white border-gray-200'
              }`}>
                <div className="p-3">
                  <div className={`text-xs font-medium uppercase tracking-wider mb-3 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Export Formats
                  </div>
                  
                  {(() => {
                    const actualReportType = activeReport === 'overview' ? 'sales' : activeReport;
                    return (
                      <>
                        <button
                          onClick={() => handleExport(activeReport, 'pdf')}
                          disabled={loadingStates[`export_${actualReportType}_pdf`]}
                          className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-all duration-200 flex items-center justify-between touch-manipulation ${
                            loadingStates[`export_${actualReportType}_pdf`]
                              ? 'opacity-50 cursor-not-allowed'
                              : theme === 'dark'
                                ? 'hover:bg-slate-700 text-gray-300'
                                : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${
                              theme === 'dark' ? 'bg-red-400' : 'bg-red-500'
                            }`}></div>
                            <span className="font-medium">PDF Document</span>
                          </div>
                          {loadingStates[`export_${actualReportType}_pdf`] && (
                            <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                          )}
                        </button>
                        
                        <button
                          onClick={() => handleExport(activeReport, 'csv')}
                          disabled={loadingStates[`export_${actualReportType}_csv`]}
                          className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-all duration-200 flex items-center justify-between touch-manipulation ${
                            loadingStates[`export_${actualReportType}_csv`]
                              ? 'opacity-50 cursor-not-allowed'
                              : theme === 'dark'
                                ? 'hover:bg-slate-700 text-gray-300'
                                : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${
                              theme === 'dark' ? 'bg-blue-400' : 'bg-blue-500'
                            }`}></div>
                            <span className="font-medium">CSV File</span>
                          </div>
                          {loadingStates[`export_${actualReportType}_csv`] && (
                            <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                          )}
                        </button>
                      </>
                    );
                  })()}
                  
                  <div className={`text-xs mt-3 pt-2 border-t ${
                    theme === 'dark' 
                      ? 'text-gray-500 border-slate-600' 
                      : 'text-gray-400 border-gray-200'
                  }`}>
                    Professional reports with your data
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Navigation */}
      <div className="flex flex-wrap gap-2 sm:gap-3">
        {[
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'sales', label: 'Sales', icon: TrendingUp },
          { key: 'financial', label: 'Financial', icon: DollarSign },
          { key: 'inventory', label: 'Inventory', icon: Package },
          { key: 'customers', label: 'Customers', icon: Users },
          { key: 'dues', label: 'Dues', icon: AlertCircle },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => handleReportChange(key as typeof activeReport)}
            className={`flex items-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg sm:rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation ${
              activeReport === key
                ? 'bg-blue-500 text-white border-blue-500 shadow-lg'
                : theme === 'dark'
                  ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 hover:border-gray-500'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <Icon size={16} />
            <span className="text-sm font-medium">{label}</span>
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="space-y-4 sm:space-y-6">
          {activeReport === 'overview' ? (
            <>
              {/* Stats cards skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className={`p-4 sm:p-6 rounded-lg sm:rounded-2xl border animate-pulse ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className={`h-3 w-20 rounded mb-2 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-5 w-28 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Top Products & Dues sections skeleton */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Products skeleton */}
                <div className={`p-6 rounded-2xl border animate-pulse ${
                  theme === 'dark'
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200'
                }`}>
                  <div className={`h-5 w-32 rounded mb-4 ${
                    theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
                  <div className="space-y-4">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className={`w-8 h-8 rounded-lg ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                          <div className="flex-1">
                            <div className={`h-4 w-24 rounded mb-1 ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                            }`} />
                            <div className={`h-3 w-32 rounded ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                            }`} />
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`h-4 w-16 rounded mb-1 ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                          <div className={`h-3 w-20 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Outstanding Dues skeleton */}
                <div className={`p-6 rounded-2xl border animate-pulse ${
                  theme === 'dark'
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200'
                }`}>
                  <div className={`h-5 w-32 rounded mb-4 ${
                    theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
                  <div className="space-y-4">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-xl ${
                          theme === 'dark'
                            ? 'bg-slate-700/50 border border-slate-600'
                            : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className={`h-4 w-24 rounded mb-1 ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                            <div className={`h-3 w-20 rounded ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </div>
                          <div className="text-right">
                            <div className={`h-4 w-16 rounded mb-1 ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                            <div className={`h-3 w-12 rounded ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : activeReport === 'sales' ? (
            <>
              {/* Title skeleton */}
              <div className={`h-5 w-32 rounded animate-pulse mb-4 ${
                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
              }`} />
              {/* Mobile card view skeleton */}
              <div className="block lg:hidden space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border animate-pulse ${
                      theme === 'dark'
                        ? 'border-slate-700 bg-slate-700/50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className={`h-4 w-24 rounded ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                      <div className={`h-4 w-16 rounded ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className={`h-3 w-12 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div>
                        <div className={`h-3 w-16 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-12 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div className="col-span-2">
                        <div className={`h-3 w-16 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-20 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view skeleton */}
              <div className="hidden lg:block overflow-x-auto">
                <table className={`w-full ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead>
                    <tr className={`border-b ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                      <th className="text-left py-2 h-4" />
                      <th className="text-right py-2 h-4" />
                      <th className="text-right py-2 h-4" />
                      <th className="text-right py-2 h-4" />
                      <th className="text-right py-2 h-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <tr
                        key={index}
                        className={`border-b animate-pulse ${theme === 'dark' ? 'border-slate-700' : 'border-gray-100'}`}
                      >
                        <td className="py-3">
                          <div className={`h-4 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-20 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-16 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-20 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-20 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : activeReport === 'financial' ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Title skeleton */}
              <div className={`h-5 w-32 rounded animate-pulse ${
                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
              }`} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border animate-pulse ${
                    theme === 'dark'
                      ? 'bg-slate-700 border-slate-600'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`h-3 w-20 rounded ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    <div className="w-4 h-4 rounded bg-slate-600" />
                  </div>
                  <div className={`h-5 w-24 rounded ${
                    theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                  }`} />
                </div>
              ))}
              </div>
            </div>
          ) : activeReport === 'inventory' ? (
            <>
              {/* Title skeleton */}
              <div className={`h-5 w-32 rounded animate-pulse mb-4 ${
                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
              }`} />
              {/* Summary cards skeleton */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 animate-pulse">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${
                      theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-4 h-4 rounded bg-slate-600 flex-shrink-0" />
                      <div className="flex-1">
                        <div className={`h-2 w-16 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-8 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile card view skeleton */}
              <div className="lg:hidden space-y-3 animate-pulse">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className={`h-4 w-24 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div className={`h-5 w-16 rounded-full ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <div className={`h-2 w-8 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-6 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div>
                        <div className={`h-2 w-12 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-6 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div>
                        <div className={`h-2 w-8 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-12 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view skeleton */}
              <div className="hidden lg:block overflow-x-auto animate-pulse">
                <table className={`w-full ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead>
                    <tr className={`border-b ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                      <th className="text-left py-3 h-4" />
                      <th className="text-left py-3 h-4" />
                      <th className="text-right py-3 h-4" />
                      <th className="text-right py-3 h-4" />
                      <th className="text-right py-3 h-4" />
                      <th className="text-center py-3 h-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <tr
                        key={index}
                        className={`border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-100'}`}
                      >
                        <td className="py-3">
                          <div className={`h-4 w-20 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="py-3">
                          <div className={`h-4 w-16 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-8 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-8 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-16 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-center py-3">
                          <div className={`h-5 w-12 rounded-full mx-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : activeReport === 'customers' ? (
            <>
              {/* Title skeleton */}
              <div className={`h-5 w-32 rounded animate-pulse mb-4 ${
                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
              }`} />
              {/* Top customers cards skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 animate-pulse">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                      theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                      <div className="flex-1">
                        <div className={`h-4 w-20 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs sm:text-sm">
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm">
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-12 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm">
                        <div className={`h-3 w-12 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile card view skeleton */}
              <div className="lg:hidden space-y-3 animate-pulse">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className={`h-4 w-24 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-5 w-16 rounded-full mt-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className={`h-2 w-12 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-6 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div>
                        <div className={`h-2 w-16 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div>
                        <div className={`h-2 w-12 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-6 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div>
                        <div className={`h-2 w-16 rounded mb-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-3 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view skeleton */}
              <div className="hidden lg:block overflow-x-auto animate-pulse">
                <table className={`w-full ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead>
                    <tr className={`border-b ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                      <th className="text-left py-3 h-4" />
                      <th className="text-center py-3 h-4" />
                      <th className="text-right py-3 h-4" />
                      <th className="text-right py-3 h-4" />
                      <th className="text-right py-3 h-4" />
                      <th className="text-center py-3 h-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <tr
                        key={index}
                        className={`border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-100'}`}
                      >
                        <td className="py-3">
                          <div className={`h-4 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-center py-3">
                          <div className={`h-5 w-12 rounded-full mx-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-8 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-16 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-right py-3">
                          <div className={`h-4 w-12 rounded ml-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                        <td className="text-center py-3">
                          <div className={`h-4 w-16 rounded mx-auto ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                          }`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : activeReport === 'dues' ? (
            <>
              {/* Title skeleton */}
              <div className={`h-5 w-32 rounded animate-pulse mb-4 ${
                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
              }`} />
              <div className="space-y-3 sm:space-y-4 animate-pulse">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                      theme === 'dark'
                        ? 'bg-slate-700/50 border-slate-600'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1">
                        {/* Customer name skeleton */}
                        <div className={`h-4 w-24 rounded mb-2 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        
                        {/* Phone, sales count, days overdue skeleton */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                          <div className={`h-3 w-20 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className={`h-3 w-16 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className={`h-3 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>
                      
                      {/* Amount + Last payment + Buttons skeleton */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div>
                          <div className={`h-5 w-20 rounded mb-1 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className={`h-3 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                        
                        {/* Action buttons skeleton */}
                        <div className="flex items-center gap-2">
                          <div className={`h-8 w-12 rounded-lg flex-shrink-0 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className={`h-8 w-12 rounded-lg flex-shrink-0 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary section skeleton */}
              <div className={`mt-6 p-3 sm:p-4 rounded-lg sm:rounded-xl animate-pulse ${
                theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                  <div className={`h-4 w-24 rounded ${
                    theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                  }`} />
                  <div className={`h-5 w-28 rounded ${
                    theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                  }`} />
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Report Content */}
      {!loading && (
        <div>
          {activeReport === 'overview' && renderOverview()}
          
          {activeReport === 'sales' && (
            <div className={`p-4 sm:p-6 rounded-lg sm:rounded-2xl border ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Sales Report
              </h3>
              <div className="space-y-4">
                {salesReport.length === 0 ? (
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                    No sales data available for the selected period.
                  </p>
                ) : (
                  <>
                    {/* Mobile Card View */}
                    <div className="block lg:hidden space-y-3">
                      {salesReport.map((report, index) => (
                        <div key={index} className={`p-3 rounded-lg border ${
                          theme === 'dark' ? 'border-slate-700 bg-slate-700/50' : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {new Date(report.date).toLocaleDateString()}
                            </div>
                            <div className={`text-sm font-bold text-green-600`}>
                              {formatCurrency(report.profit)}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Sales:</span>
                              <span className={`ml-1 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {formatCurrency(report.total_sales)}
                              </span>
                            </div>
                            <div>
                              <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Transactions:</span>
                              <span className={`ml-1 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {report.total_transactions}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Avg. Value:</span>
                              <span className={`ml-1 font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {formatCurrency(report.average_transaction_value)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className={`w-full ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <thead>
                          <tr className={`border-b ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                            <th className="text-left py-2">Date</th>
                            <th className="text-right py-2">Sales</th>
                            <th className="text-right py-2">Transactions</th>
                            <th className="text-right py-2">Avg. Value</th>
                            <th className="text-right py-2">Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesReport.map((report, index) => (
                            <tr key={index} className={`border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-100'}`}>
                              <td className="py-3">{new Date(report.date).toLocaleDateString()}</td>
                              <td className="text-right py-3">{formatCurrency(report.total_sales)}</td>
                              <td className="text-right py-3">{report.total_transactions}</td>
                              <td className="text-right py-3">{formatCurrency(report.average_transaction_value)}</td>
                              <td className="text-right py-3 text-green-600">{formatCurrency(report.profit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeReport === 'financial' && financialSummary && (
            <div className={`p-4 sm:p-6 rounded-lg sm:rounded-2xl border ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-4 sm:mb-6 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Financial Summary
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                {/* Total Revenue */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                  theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-xs sm:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Total Revenue
                    </h4>
                    <DollarSign size={14} className="text-blue-500 sm:w-4 sm:h-4" />
                  </div>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {formatCurrency(financialSummary.total_revenue)}
                  </p>
                </div>

                {/* Gross Profit */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                  theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-xs sm:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Gross Profit
                    </h4>
                    <TrendingUp size={14} className="text-green-500 sm:w-4 sm:h-4" />
                  </div>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                    theme === 'dark' ? 'text-green-400' : 'text-green-600'
                  }`}>
                    {formatCurrency(financialSummary.gross_profit)}
                  </p>
                </div>

                {/* Profit Margin */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                  theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-xs sm:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Profit Margin
                    </h4>
                    <BarChart3 size={14} className="text-purple-500 sm:w-4 sm:h-4" />
                  </div>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                    theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                  }`}>
                    {financialSummary.profit_margin.toFixed(1)}%
                  </p>
                </div>

                {/* Total Cost */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                  theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-xs sm:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Total Cost
                    </h4>
                    <Package size={14} className="text-red-500 sm:w-4 sm:h-4" />
                  </div>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}>
                    {formatCurrency(financialSummary.total_cost)}
                  </p>
                </div>

                {/* Tax Collected */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                  theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-xs sm:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Tax Collected
                    </h4>
                    <AlertCircle size={14} className="text-yellow-500 sm:w-4 sm:h-4" />
                  </div>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                    theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                  }`}>
                    {formatCurrency(financialSummary.tax_collected)}
                  </p>
                </div>

                {/* Net Profit */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                  theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-xs sm:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Net Profit
                    </h4>
                    <TrendingUp size={14} className="text-emerald-500 sm:w-4 sm:h-4" />
                  </div>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                    theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                  }`}>
                    {formatCurrency(financialSummary.net_profit)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeReport === 'inventory' && (
            <div className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-lg sm:text-xl font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Inventory Report
              </h3>
              
              {inventoryReport.length === 0 ? (
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                  No inventory data available.
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${
                      theme === 'dark' ? 'bg-slate-700/50' : 'bg-blue-50'
                    }`}>
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <Package className="text-blue-500" size={16} />
                        <div>
                          <p className={`text-xs sm:text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-blue-600'
                          }`}>
                            Total Items
                          </p>
                          <p className={`text-sm sm:text-lg font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-blue-900'
                          }`}>
                            {inventoryReport.length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${
                      theme === 'dark' ? 'bg-slate-700/50' : 'bg-green-50'
                    }`}>
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <TrendingUp className="text-green-500" size={16} />
                        <div>
                          <p className={`text-xs sm:text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-green-600'
                          }`}>
                            In Stock
                          </p>
                          <p className={`text-sm sm:text-lg font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-green-900'
                          }`}>
                            {inventoryReport.filter(item => item.status === 'in-stock').length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${
                      theme === 'dark' ? 'bg-slate-700/50' : 'bg-yellow-50'
                    }`}>
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <AlertCircle className="text-yellow-500" size={16} />
                        <div>
                          <p className={`text-xs sm:text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-yellow-600'
                          }`}>
                            Low Stock
                          </p>
                          <p className={`text-sm sm:text-lg font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-yellow-900'
                          }`}>
                            {inventoryReport.filter(item => item.status === 'low-stock').length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${
                      theme === 'dark' ? 'bg-slate-700/50' : 'bg-red-50'
                    }`}>
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <AlertCircle className="text-red-500" size={16} />
                        <div>
                          <p className={`text-xs sm:text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-red-600'
                          }`}>
                            Out of Stock
                          </p>
                          <p className={`text-sm sm:text-lg font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-red-900'
                          }`}>
                            {inventoryReport.filter(item => item.status === 'out-of-stock').length}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {inventoryReport.map((item) => (
                      <div key={item.product_id} className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className={`font-medium text-sm ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                              {item.product_name}
                            </h4>
                            <p className={`text-xs ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {item.brand}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'in-stock'
                              ? 'bg-green-100 text-green-800'
                              : item.status === 'low-stock'
                              ? 'bg-yellow-100 text-yellow-800'
                              : item.status === 'out-of-stock'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {item.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className={`block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              Stock
                            </span>
                            <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {item.current_stock}
                            </span>
                          </div>
                          <div>
                            <span className={`block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              Min Level
                            </span>
                            <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {item.minimum_stock_level}
                            </span>
                          </div>
                          <div>
                            <span className={`block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              Value
                            </span>
                            <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {formatCurrency(item.total_value)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className={`w-full ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      <thead>
                        <tr className={`border-b ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                          <th className="text-left py-3">Product</th>
                          <th className="text-left py-3">Brand</th>
                          <th className="text-right py-3">Stock</th>
                          <th className="text-right py-3">Min Level</th>
                          <th className="text-right py-3">Value</th>
                          <th className="text-center py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryReport.map((item) => (
                          <tr key={item.product_id} className={`border-b ${
                            theme === 'dark' ? 'border-slate-700' : 'border-gray-100'
                          }`}>
                            <td className="py-3 font-medium">{item.product_name}</td>
                            <td className="py-3 text-sm text-gray-500">{item.brand}</td>
                            <td className="text-right py-3">{item.current_stock}</td>
                            <td className="text-right py-3">{item.minimum_stock_level}</td>
                            <td className="text-right py-3">{formatCurrency(item.total_value)}</td>
                            <td className="text-center py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.status === 'in-stock'
                                  ? 'bg-green-100 text-green-800'
                                  : item.status === 'low-stock'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : item.status === 'out-of-stock'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {item.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeReport === 'customers' && (
            <div>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6">
                <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border ${
                  theme === 'dark'
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
                      theme === 'dark' 
                        ? 'bg-blue-900/50 text-blue-400' 
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Customers
                      </p>
                      <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                        {customerReport.length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border ${
                  theme === 'dark'
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
                      theme === 'dark' 
                        ? 'bg-green-900/50 text-green-400' 
                        : 'bg-green-100 text-green-600'
                    }`}>
                      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Spent
                      </p>
                      <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                        {formatCurrency(customerReport.reduce((sum, c) => sum + c.total_amount, 0))}
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border ${
                  theme === 'dark'
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
                      theme === 'dark' 
                        ? 'bg-red-900/50 text-red-400' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        With Balance
                      </p>
                      <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                        {customerReport.filter(c => c.dues > 0).length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border col-span-2 lg:col-span-1 ${
                  theme === 'dark'
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
                      theme === 'dark' 
                        ? 'bg-orange-900/50 text-orange-400' 
                        : 'bg-orange-100 text-orange-600'
                    }`}>
                      <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total Dues
                      </p>
                      <div className="relative inline-block group">
                        <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate cursor-help`}>
                          {formatCurrency(customerReport.reduce((sum, c) => sum + c.dues, 0))}
                        </p>
                        {/* Tooltip */}
                        <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ${
                          theme === 'dark'
                            ? 'bg-slate-900 text-slate-100 border border-slate-700'
                            : 'bg-gray-900 text-white border border-gray-800'
                        }`}>
                          {formatCurrency(customerReport.reduce((sum, c) => sum + c.dues, 0))}
                          <div className={`absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent ${
                            theme === 'dark'
                              ? 'border-t-slate-900'
                              : 'border-t-gray-900'
                          }`} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Report Card */}
              <div className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-lg sm:text-xl font-semibold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Customer Details
                </h3>
                
                {customerReport.length === 0 ? (
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                    No customer data available for the selected period.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {/* Filtered customer list - filtered by the customerType state */}
                    {customerReport
                      .filter(c => !customerType || customerType === 'all' || c.customer_type === customerType)
                      .length === 0 ? (
                      <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                        No customers found for type: {customerType}
                      </p>
                    ) : (
                      <>
                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-3">
                          {customerReport
                            .filter(c => !customerType || customerType === 'all' || c.customer_type === customerType)
                            .map((customer) => (
                              <div key={customer.customer_id} className={`p-4 rounded-lg border ${
                                theme === 'dark' ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                              }`}>
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex-1">
                                    <h4 className={`font-medium text-sm ${
                                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                      {customer.customer_name}
                                    </h4>
                                    <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium ${
                                      customer.customer_type === 'retail'
                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                        : customer.customer_type === 'home'
                                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                        : customer.customer_type === 'wholesale'
                                        ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
                                        : customer.customer_type === 'business'
                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                        : customer.customer_type === 'vip'
                                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                    }`}>
                                      {formatCustomerType(customer.customer_type || 'Regular')}
                                    </span>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                  <div>
                                    <span className={`block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Purchases
                                    </span>
                                    <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                      {customer.total_purchases}
                                    </span>
                                  </div>
                                  <div>
                                    <span className={`block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Total Amount
                                    </span>
                                    <span className="font-semibold text-green-600">
                                      {formatCurrency(customer.total_amount)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className={`block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Balance
                                    </span>
                                    <span className={customer.dues > 0 ? "font-semibold text-red-600" : "text-green-600"}>
                                      {formatCurrency(customer.dues)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className={`block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Last Purchase
                                    </span>
                                    <span className={`text-xs ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                      {customer.last_purchase_date 
                                        ? new Date(customer.last_purchase_date).toLocaleDateString()
                                        : 'Never'
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden lg:block overflow-x-auto">
                          <table className={`w-full ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            <thead>
                              <tr className={`border-b ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                                <th className="text-left py-3">Customer</th>
                                <th className="text-center py-3">Type</th>
                                <th className="text-right py-3">Purchases</th>
                                <th className="text-right py-3">Total Amount</th>
                                <th className="text-right py-3">Balance</th>
                                <th className="text-center py-3">Last Purchase</th>
                              </tr>
                            </thead>
                            <tbody>
                              {customerReport
                                .filter(c => !customerType || customerType === 'all' || c.customer_type === customerType)
                                .map((customer) => (
                                  <tr key={customer.customer_id} className={`border-b ${
                                    theme === 'dark' ? 'border-slate-700' : 'border-gray-100'
                                  }`}>
                                    <td className="py-3 font-medium">{customer.customer_name}</td>
                                    <td className="text-center py-3">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        customer.customer_type === 'retail'
                                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                          : customer.customer_type === 'home'
                                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                          : customer.customer_type === 'wholesale'
                                          ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
                                          : customer.customer_type === 'business'
                                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                          : customer.customer_type === 'vip'
                                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                      }`}>
                                        {formatCustomerType(customer.customer_type || 'Regular')}
                                      </span>
                                    </td>
                                    <td className="text-right py-3">{customer.total_purchases}</td>
                                    <td className="text-right py-3 font-semibold text-green-600">
                                      {formatCurrency(customer.total_amount)}
                                    </td>
                                    <td className="text-right py-3">
                                      {customer.dues > 0 ? (
                                        <span className="font-semibold text-red-600">
                                          {formatCurrency(customer.dues)}
                                        </span>
                                      ) : (
                                        <span className="text-green-600">{formatCurrency(0)}</span>
                                      )}
                                    </td>
                                    <td className="text-center py-3 text-sm">
                                      {customer.last_purchase_date 
                                        ? new Date(customer.last_purchase_date).toLocaleDateString()
                                        : 'Never'
                                      }
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeReport === 'dues' && (
            <div className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-lg sm:text-xl font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Outstanding Dues Report
              </h3>
              <div className="space-y-3 sm:space-y-4">
                {duesReport
                  .filter(due => !customerType || customerType === 'all' || due.customer_type === customerType)
                  .length === 0 ? (
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                    No outstanding dues for type: {customerType}
                  </p>
                ) : (
                  <>
                    {duesReport
                      .filter(due => !customerType || customerType === 'all' || due.customer_type === customerType)
                      .map((due) => (
                  <div key={due.customer_id} className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
                    due.days_overdue > 7
                      ? theme === 'dark'
                        ? 'bg-red-900/20 border-red-800'
                        : 'bg-red-50 border-red-200'
                      : theme === 'dark'
                        ? 'bg-slate-700/50 border-slate-600'
                        : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 gap-3">
                      <div className="flex-1">
                        <h4 className={`font-medium text-sm sm:text-base ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {due.customer_name}
                        </h4>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-xs sm:text-sm mt-1 space-y-1 sm:space-y-0">
                          {due.phone && (
                            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                              📞 {due.phone}
                            </span>
                          )}
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                            {due.sales_count || 0} sales
                          </span>
                          {due.days_overdue > 0 && (
                            <span className={`font-medium ${
                              due.days_overdue > 7 ? 'text-red-500' : 'text-yellow-500'
                            }`}>
                              {due.days_overdue} days overdue
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="text-left sm:text-right">
                          <p className="text-lg sm:text-xl font-bold text-red-500">
                            {formatCurrency(due.total_due)}
                          </p>
                          {due.last_payment_date && (
                            <p className={`text-xs ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Last payment: {new Date(due.last_payment_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleCustomerReportExport(due.customer_id, due.customer_name, 'pdf')}
                            disabled={loadingStates[`customer_${due.customer_id}_pdf`]}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              loadingStates[`customer_${due.customer_id}_pdf`]
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            } ${
                              theme === 'dark'
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                            title="Download PDF Report"
                          >
                            <Download size={14} />
                            <span>PDF</span>
                          </button>
                          <button
                            onClick={() => handleCustomerReportExport(due.customer_id, due.customer_name, 'csv')}
                            disabled={loadingStates[`customer_${due.customer_id}_csv`]}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              loadingStates[`customer_${due.customer_id}_csv`]
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            } ${
                              theme === 'dark'
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                            title="Download CSV Report"
                          >
                            <Download size={14} />
                            <span>CSV</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                  </>
                )}
              </div>
              
              {/* Summary */}
              <div className={`mt-6 p-3 sm:p-4 rounded-lg sm:rounded-xl ${
                theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                  <span className={`font-medium text-sm sm:text-base ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Total Outstanding
                  </span>
                  <span className={`text-lg sm:text-xl font-bold ${
                    theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                  }`}>
                    {formatCurrency(duesReport
                      .filter(due => !customerType || customerType === 'all' || due.customer_type === customerType)
                      .reduce((sum, due) => sum + due.total_due, 0)
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
